import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { useCookieConsent } from '../contexts/CookieConsentContext';
const buttonBase = 'inline-flex items-center justify-center whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mint-700';
const primary = `${buttonBase} bg-mint-600 text-white hover:bg-mint-700`;
const secondary = `${buttonBase} bg-ink text-white hover:bg-mint-900`;
const outline = `${buttonBase} bg-white text-mint-800 ring-1 ring-inset ring-mint-300 hover:bg-mint-50`;
export function CookieBanner() {
  const reduceMotion = useReducedMotion();
  const {
    consent,
    isBannerVisible,
    isSettingsOpen,
    acceptAll,
    rejectAll,
    savePreferences,
    openSettings,
    closeSettings
  } = useCookieConsent();
  const [marketing, setMarketing] = useState<boolean>(consent?.marketing ?? false);
  useEffect(() => {
    if (isSettingsOpen) setMarketing(consent?.marketing ?? false);
  }, [isSettingsOpen, consent]);
  return <AnimatePresence data-id="element-47" data-mp="owpf162k9nzr">
      {isBannerVisible ? <motion.div key="cookie-banner" role="dialog" aria-modal="false" aria-labelledby="cookie-title" aria-describedby="cookie-description" initial={reduceMotion ? {
      opacity: 0
    } : {
      opacity: 0,
      y: 24
    }} animate={reduceMotion ? {
      opacity: 1
    } : {
      opacity: 1,
      y: 0
    }} exit={reduceMotion ? {
      opacity: 0
    } : {
      opacity: 0,
      y: 24
    }} transition={{
      duration: 0.24,
      ease: [0.23, 1, 0.32, 1]
    }} className="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-6" data-id="element-48" data-mp="ttpl3gzeuqqv">
          <div className="relative mx-auto max-w-3xl rounded-3xl border border-mint-200 bg-white p-6 shadow-lift sm:p-8" data-id="element-49" data-mp="3sati5ym769pk">
            {isSettingsOpen && consent ? <button type="button" onClick={closeSettings} className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full text-ink-muted transition-colors duration-150 ease-out hover:bg-mint-50 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-mint-700" aria-label="Cerrar configuración de cookies" data-id="element-50" data-mp="3d8jxogn2c6nl">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-51" data-mp="2zgn9gnoimgwh" />
              </button> : null}

            <h2 id="cookie-title" className="font-display text-2xl text-ink" data-id="element-52" data-mp="2uivaxatd7wjb">
              {isSettingsOpen ? 'Configurar cookies' : 'Tu privacidad'}
            </h2>

            {isSettingsOpen ? <>
                <p id="cookie-description" className="mt-3 text-[15px] leading-relaxed text-ink-soft" data-id="element-53" data-mp="2lhxbl5tvgd72">
                  Elige qué herramientas permites. Puedes cambiar tu elección en
                  cualquier momento.
                </p>
                <ul className="mt-6 divide-y divide-mint-100 border-y border-mint-100" data-id="element-54" data-mp="eqxv0brq4x4b">
                  <li className="flex items-start justify-between gap-6 py-4" data-id="element-55" data-mp="ep3cf43xdh3e">
                    <div data-id="element-56" data-mp="ma6xdl1tbvjv">
                      <p className="font-semibold text-ink" data-id="element-57" data-mp="r9jrmqv0k4wh">Necesarias</p>
                      <p className="mt-1 text-sm leading-relaxed text-ink-soft" data-id="element-58" data-mp="r7grvf426xek">
                        Guardan tu elección sobre cookies en este navegador.
                      </p>
                    </div>
                    <span className="shrink-0 whitespace-nowrap pt-0.5 text-sm font-medium text-mint-700" data-id="element-59" data-mp="111604ffyvpvj">
                      Siempre activas
                    </span>
                  </li>
                  <li className="flex items-start justify-between gap-6 py-4" data-id="element-60" data-mp="8cs2w1wpcxl1">
                    <div data-id="element-61" data-mp="d87esob5gjet">
                      <p id="cookie-marketing-label" className="font-semibold text-ink" data-id="element-62" data-mp="dc4wr6lzr4x4">
                        Publicidad (Meta Pixel)
                      </p>
                      <p className="mt-1 text-sm leading-relaxed text-ink-soft" data-id="element-63" data-mp="1oo5tp8t0dzny">
                        Mide visitas y conversiones de nuestros anuncios en
                        Facebook e Instagram. Proveedor: Meta Platforms Ireland Ltd.
                      </p>
                    </div>
                    <button type="button" role="switch" aria-checked={marketing} aria-labelledby="cookie-marketing-label" onClick={() => setMarketing(value => !value)} className={`relative mt-0.5 inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mint-700 ${marketing ? 'bg-mint-600' : 'bg-mint-200'}`} data-id="element-64" data-mp="1mbkcnkr1uyzo">
                      <span className={`inline-block h-5 w-5 rounded-full bg-white shadow-soft transition-transform duration-150 ease-out ${marketing ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-65" data-mp="2jy180kg832is" />
                    </button>
                  </li>
                </ul>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-end" data-id="element-66" data-mp="2sz8snzptg4ue">
                  <button type="button" onClick={rejectAll} className={secondary} data-id="element-67" data-mp="2oyoz164gbjd0">
                    Rechazar todas
                  </button>
                  <button type="button" onClick={() => savePreferences(marketing)} className={outline} data-id="element-68" data-mp="3ck92sei6swk2">
                    Guardar selección
                  </button>
                  <button type="button" onClick={acceptAll} className={primary} data-id="element-69" data-mp="2ymu92xkwi2ma">
                    Aceptar todas
                  </button>
                </div>
              </> : <>
                <p id="cookie-description" className="mt-3 text-[15px] leading-relaxed text-ink-soft" data-id="element-70" data-mp="2otdbrjlk25dv">
                  Usamos almacenamiento técnico necesario para recordar tu
                  elección. Con tu permiso, también usaremos Meta Pixel para medir
                  la eficacia de nuestros anuncios. No se activa ninguna
                  herramienta publicitaria hasta que la aceptes. Puedes cambiar tu
                  elección cuando quieras desde «Configurar cookies», al pie de la
                  página.{' '}
                  <Link to="/legal/cookies" className="font-semibold text-mint-700 underline underline-offset-4 hover:text-mint-800" data-id="element-71" data-mp="gex3wllygz3n">
                    Política de cookies
                  </Link>
                </p>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end" data-id="element-72" data-mp="7m3mahj1dnmm">
                  <button type="button" onClick={openSettings} className={outline} data-id="element-73" data-mp="2niik84ksltf">
                    Configurar
                  </button>
                  <button type="button" onClick={rejectAll} className={secondary} data-id="element-74" data-mp="zchvi0g3ubry">
                    Rechazar
                  </button>
                  <button type="button" onClick={acceptAll} className={primary} data-id="element-75" data-mp="vad478d4pr8h">
                    Aceptar
                  </button>
                </div>
              </>}
          </div>
        </motion.div> : null}
    </AnimatePresence>;
}